/**
 * editAvatarModal.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import * as React from "react";

//import FormControl from "@mui/material/FormControl";
//import FormLabel from "@mui/material/FormLabel";
//import MenuItem from "@mui/material/MenuItem";
//import Select, { SelectChangeEvent } from "@mui/material/Select";
import { Button, Slider } from "@mui/material";

import { AuthContext } from "../auth/auth.context";

import { Dialog } from "../components/dialog.component";

import { authServiceFactory } from "../services/auth.service";
import { base64ToBuffer } from "../services/encoding.service";

interface Props {
  open: boolean,
  didChangeAvatar(url: string): void,
  onClose(): void
}

interface State {
  copiedUrl: string,
  scale: number,
  localImg: HTMLImageElement | null,
  saving: boolean
}

export class EditAvatarModal extends React.Component<Props, State> {
  private static DISPLAY_WIDTH = 300;
  private canvasRef: React.RefObject<HTMLCanvasElement | null>;

  static contextType: React.Context<AuthContext> | undefined = AuthContext;
  declare context: React.ContextType<typeof AuthContext>;

  private dragStartX: number;
  private dragStartY: number;
  private curDX: number;
  private curDY: number;

  constructor(props: Props) {
    super(props);
    this.canvasRef = React.createRef<HTMLCanvasElement>();
    this.dragStartX = -1;
    this.dragStartY = -1;
    this.curDX = 0;
    this.curDY = 0;

    this.onConfirm = this.onConfirm.bind(this);
    //this.selectedExisting = this.selectedExisting.bind(this);
    this.loadedFile = this.loadedFile.bind(this);
    this.changedScale = this.changedScale.bind(this);
    this.dragImageStart = this.dragImageStart.bind(this);
    this.dragImageEnd = this.dragImageEnd.bind(this);
    this.dragImage = this.dragImage.bind(this);

    this.state = {
      copiedUrl: "",
      scale: 1,
      localImg: null,
      saving: false
    };
  }

  componentWillUnmount () {
    if (this.state.localImg) {
      URL.revokeObjectURL(this.state.localImg.src);
    }
  }

  componentDidUpdate (prevProps: Props, prevState: State) {
    if (prevProps.open && !this.props.open) {
      // closing
      if (this.state.localImg) {
        URL.revokeObjectURL(this.state.localImg.src);
      }
      this.setState({
        localImg: null,
        scale: 1
      });
      this.curDX = 0;
      this.curDY = 0;
      this.dragStartX = -1;
      this.dragStartY = -1;
    }
    if (prevState.scale !== this.state.scale && this.state.localImg) {
      this.renderImage(this.state.localImg);
    }
  }

  loadedFile(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    if (!inputElem.files || inputElem.files.length <= 0) {
      return;
    }
    if (!this.canvasRef.current) { return; }

    const image = new Image();
    image.onload = () => {
      this.renderImage(image);
    };
    const url = URL.createObjectURL(inputElem.files[0]);
    this.setState({localImg: image});
    image.src = url;
  }

  /*selectedExisting(event: SelectChangeEvent) {
    console.log("selected:", event.target.value);
    this.props.onClose();
  }*/

  async onConfirm() {
    if (!this.state.localImg || !this.canvasRef.current ||
      !this.context || !this.context.membership) { return; }

    try {
      const ctx = this.canvasRef.current.getContext("2d");
      if (!ctx) { return; }
      const contentType = "image/png";
      const dataUrl = this.canvasRef.current.toDataURL(contentType);
      if (!dataUrl.startsWith("data:image/png;base64,")) {
        console.error("Unexpected data url:", dataUrl);
        return;
      }
      const buf = base64ToBuffer(dataUrl.substring(22));

      this.setState({saving: true});
      await authServiceFactory().uploadAvatar(this.context.membership, contentType, buf);

      this.props.didChangeAvatar(this.context.membership.avatarUrl || "");
    } catch (err) {
      console.error("Error saving avatar:", err);
      this.setState({saving: false});
    }
  }

  scaledDimensions(image: HTMLImageElement, newScale?: number): [number, number] {
    let width: number;
    let height: number;

    const scale = newScale || this.state.scale;

    if (image.height > image.width) {
      // scale image to fit width
      width = EditAvatarModal.DISPLAY_WIDTH * scale;
      height = width * image.height / image.width;
    } else {
      // scale image to fit height
      height = EditAvatarModal.DISPLAY_WIDTH * scale; // 'width' since avatars are square
      width = height * image.width / image.height;
    }
    return [width, height];
  }

  renderImage(image: HTMLImageElement, w?: number, h?: number) {
    if (!this.canvasRef.current) { return; }
    const [width, height] = w && h ? [w, h] : this.scaledDimensions(image);
    const ctx = this.canvasRef.current.getContext("2d");
    ctx?.clearRect(0, 0, EditAvatarModal.DISPLAY_WIDTH, EditAvatarModal.DISPLAY_WIDTH);
    ctx?.drawImage(image, this.curDX, this.curDY, width, height);
  }

  setOffsets(dx: number, dy: number, width: number, height: number) {
    if (dx > 0) {
      this.curDX = 0;
    } else if (dx < EditAvatarModal.DISPLAY_WIDTH - width) {
      this.curDX = EditAvatarModal.DISPLAY_WIDTH - width;
    } else {
      this.curDX = dx;
    }

    if (dy > 0) {
      this.curDY = 0;
    } else if (dy < EditAvatarModal.DISPLAY_WIDTH - height) {
      this.curDY = EditAvatarModal.DISPLAY_WIDTH - height;
    } else {
      this.curDY = dy;
    }
  }

  changedScale(event: Event) {
    if (!event.target || !("value" in event.target)) {
      console.log("no target:", event);
      return;
    }
    const newScale = (event.target as any).value;
    if (this.state.localImg) {
      const center = EditAvatarModal.DISPLAY_WIDTH / 2;
      const oldScale = this.state.scale;
      const [width, height] = this.scaledDimensions(this.state.localImg, newScale);
      const dx = center - (center - this.curDX) * newScale / oldScale;
      const dy = center - (center - this.curDY) * newScale / oldScale;
      this.setOffsets(dx, dy, width, height);
    }
    this.setState({scale: newScale});
  }
  dragImageStart(event: React.MouseEvent) {
    this.dragStartX = event.pageX - this.curDX;
    this.dragStartY = event.pageY - this.curDY;
  }
  dragImageEnd() {
    this.dragStartX = -1;
    this.dragStartY = -1;
  }
  dragImage(event: React.MouseEvent) {
    if (this.dragStartX < 0) { return; }
    if (!this.canvasRef.current || !this.state.localImg) { return; }
    event.preventDefault();
    event.stopPropagation();
    const image = this.state.localImg;
    const [width, height] = this.scaledDimensions(image);
    const dx = event.pageX - this.dragStartX;
    const dy = event.pageY - this.dragStartY;
    this.setOffsets(dx, dy, width, height);
    this.renderImage(image, width, height);
  }

  render(): React.ReactElement<any, any> {
    /*const authContext = this.context as AuthContext;
    let membershipsWithUrls: Array<Membership> = [];
    if (authContext && authContext.memberships) {
      membershipsWithUrls = authContext.memberships.filter(
        m => m.avatarUrl && m.accountID !== authContext.membership?.accountID);
    }*/

    return <Dialog open={this.props.open} title="Create New Avatar" loading={false}
        classNames={"avatar-edit-modal" + (this.state.localImg ? " haveImage" : "")}
        disabledSuccess={this.state.localImg === null}
        onClose={this.props.onClose} onConfirm={this.onConfirm}
        onMouseMove={this.dragImage}
        onMouseUp={this.dragImageEnd}>
      <input id="avatar-upload-input" accept="image/*" type="file" hidden
        data-testid="file-input" onChange={this.loadedFile}/>

      {!this.state.localImg &&
        <form className="form-wrapper">
          <div className="form-row">
            <label htmlFor="avatar-upload-input">
              <Button component="span">Choose Image</Button>
            </label>
          </div>
        </form>
      }
      <div className="avatar-edit-controls">
        <div className="avatar-canvas-container"
            onMouseDown={this.dragImageStart}>
          <canvas ref={this.canvasRef} width="300" height="300"
            />
        </div>
        <Slider
          size="small"
          defaultValue={1}
          value={this.state.scale}
          aria-label="Small"
          valueLabelDisplay="off"
          min={1}
          max={20}
          step={0.00001}
          onChange={this.changedScale}
        />
        <footer className="fine-print">
          Please Note: Avatar images will be publicly cached and unencrypted.
        </footer>
      </div>
    </Dialog>;
  }
}
/*{membershipsWithUrls.length > 0 &&
            <div className="form-row">
              <FormControl fullWidth>
                <FormLabel id="avatar-select-label">Copy Existing:</FormLabel>
                <Select
                  labelId="avatar-select-label"
                  id="avatar-select"
                  value={this.state.copiedUrl}
                  label="Copy Existing:"
                  onChange={this.selectedExisting}
                >
                  {membershipsWithUrls.map(membership =>
                    <MenuItem key={membership.accountID} value={membership.avatarUrl}>
                      {membership.accountname}
                    </MenuItem>)}
                </Select>
              </FormControl>
            </div>}*/
